/**
 * Turns checkout cross-sell API responses into ready-to-render tiles (TRAV23-4194).
 *
 * The backend exposes one endpoint per booked product ("origin"). For speed we
 * call it once per offered vertical (see `OFFERED_VERTICALS`), passing the
 * vertical as the `Type` selector, and each call returns that vertical's
 * recommendations near the booked place. The backend already applies the
 * org/site enable/disable rules, so a disabled vertical simply returns nothing —
 * the front no longer gates on any settings.
 *
 * Each tile links to the target product's search page with the API-provided
 * `searchParams` query string (plus the persisted key/lifeStyle params).
 */

import {
  ACTIVITIES_BASEADDRESSSITE_FIELD_NAME,
  CrossSellOrigin,
  CrossSellProduct,
  IAccount,
  IActivityCrossSellResponse,
  ICarCrossSellResponse,
  ICrossSellActivity,
  ICrossSellCar,
  ICrossSellLodging,
  ICrossSellTile,
  ILodgingCrossSellResponse,
} from '@share/common-types';
import { getPreferenceFromAccount } from './accounts';

/** Search-page routes, shared by both fronts (hotels & activities both use `/search`). */
const ROUTE_PATHS: Record<CrossSellProduct, string> = {
  hotel: '/search',
  condo: '/condo/deals',
  carCity: '/cars/search',
  carAirport: '/cars/search',
  activity: '/search',
};

/** The ticket samples omit price; probe the likely field names defensively. */
const PRICE_FIELDS = ['startingPrice', 'fromPrice', 'minPrice', 'lowestPrice', 'price', 'amount'];

// TODO(TRAV23-4194): source this cap from the client account preferences.
// Default 4 to stay consistent with the activities front; here no origin offers
// more than 3 verticals, so the cap never binds in practice.
const DEFAULT_MAX_TILES = 4;

/**
 * Which products to offer (and therefore which `Type` calls to fire) per booked product.
 * Cars are fanned out as two independent calls — CarCity and CarAirport — so each renders
 * its own widget, matching the backend contract (there is no single `Car` selector).
 */
export const OFFERED_VERTICALS: Record<CrossSellOrigin, CrossSellProduct[]> = {
  hotel: ['carCity', 'carAirport', 'activity'],
  condo: ['carCity', 'carAirport', 'activity'],
  activity: ['hotel', 'condo', 'carCity', 'carAirport'],
  car: ['hotel', 'condo', 'activity'],
};

/** Backend `Type` vertical-selector value for each product (see `CrossSellTargetType`). */
export const CROSS_SELL_VERTICAL_TYPE: Record<CrossSellProduct, string> = {
  hotel: 'Hotel',
  condo: 'Condo',
  carCity: 'CarCity',
  carAirport: 'CarAirport',
  activity: 'Activity',
};

const extractPrice = (obj: Record<string, unknown> | undefined): number | undefined => {
  if (!obj) return undefined;
  for (const field of PRICE_FIELDS) {
    const value = obj[field];
    if (typeof value === 'number' && value > 0) return value;
    if (typeof value === 'string' && value.trim() !== '' && Number(value) > 0) return Number(value);
  }
  return undefined;
};

export interface ICrossSellTileContext {
  /** Account segment of the URL, e.g. `/acme` (empty string when absent). */
  accountPath: string;
  /** Persisted key/lifeStyle query string from `GetHomeParams(account)`, e.g. `?token=…`. */
  homeParams: string;
  /** Products served by the current app (linked relative to the current origin). */
  sameOriginProducts: CrossSellProduct[];
  /** Base host for products served by another app (e.g. Activities front for RSI). */
  hosts?: Partial<Record<CrossSellProduct, string>>;
  /** Currency used for the tile starting price. */
  currency?: string;
}

export type CrossSellResponse =
  | ILodgingCrossSellResponse
  | ICarCrossSellResponse
  | IActivityCrossSellResponse;

/** One prefetched vertical result: the offered product and its raw API response. */
export interface ICrossSellEntry {
  vertical: CrossSellProduct;
  response: CrossSellResponse | null;
}

const stripQuery = (value?: string): string => (value ?? '').replace(/^\?/, '').trim();

/**
 * Builds a deep link to a product's search page. Same-origin products link
 * relatively; cross-app products require a configured host — when missing we
 * return `undefined` so the tile is dropped rather than pointing at the wrong app.
 */
const buildUrl = (
  product: CrossSellProduct,
  searchParams: string | undefined,
  ctx: ICrossSellTileContext,
): string | undefined => {
  const params = stripQuery(searchParams);
  if (!params) return undefined;

  let host = '';
  if (!ctx.sameOriginProducts.includes(product)) {
    host = (ctx.hosts?.[product] ?? '').replace(/\/$/, '');
    if (!host) return undefined;
  }

  const home = stripQuery(ctx.homeParams);
  const query = [params, home].filter(Boolean).join('&');

  return `${host}${ctx.accountPath}${ROUTE_PATHS[product]}${query ? `?${query}` : ''}`;
};

/** rank ascending, then score descending. */
const byRelevance = <T extends { rank?: number; score?: number }>(a: T, b: T): number => {
  const rankA = a.rank ?? Number.MAX_SAFE_INTEGER;
  const rankB = b.rank ?? Number.MAX_SAFE_INTEGER;
  if (rankA !== rankB) return rankA - rankB;
  return (b.score ?? 0) - (a.score ?? 0);
};

const pickBest = <T extends { rank?: number; score?: number }>(items: T[] | undefined): T | undefined =>
  items?.length ? [...items].sort(byRelevance)[0] : undefined;

/** The `kind` each car product accepts, matching the backend CarCity/CarAirport filter. */
const CAR_KIND: Record<'carCity' | 'carAirport', 'city' | 'airport'> = {
  carCity: 'city',
  carAirport: 'airport',
};

const carTile = (
  product: 'carCity' | 'carAirport',
  car: ICrossSellCar,
  ctx: ICrossSellTileContext,
): ICrossSellTile | undefined => {
  const url = buildUrl(product, car.searchParams, ctx);
  if (!url) return undefined;
  const name = (car.name || '').trim();
  return {
    product,
    title: name,
    destination: name,
    url,
    startingPrice: extractPrice(car),
    currency: ctx.currency,
    resultCount: car.resultCount,
    distanceMiles: car.distanceMiles,
  };
};

/**
 * One tile per distinct car location of the product's kind, best-first, deduped by
 * carLocationId. Mirrors the Notifications email (CrossSellEmailService), which lists
 * every distinct car location grouped by (kind, carLocationId) instead of a single best —
 * so a destination with both an international and a local/domestic airport surfaces both
 * tiles. The global max-tiles cap (see `finalize`) still bounds the total rendered.
 */
const carTilesByKind = (
  cars: ICrossSellCar[],
  product: 'carCity' | 'carAirport',
  ctx: ICrossSellTileContext,
): (ICrossSellTile | undefined)[] => {
  const wanted = CAR_KIND[product];
  const bestByLocation = new Map<number, ICrossSellCar>();
  for (const car of cars) {
    if ((car.kind || '').toLowerCase() !== wanted) continue;
    const existing = bestByLocation.get(car.carLocationId);
    if (!existing || byRelevance(car, existing) < 0) bestByLocation.set(car.carLocationId, car);
  }
  return [...bestByLocation.values()].sort(byRelevance).map((car) => carTile(product, car, ctx));
};

const activityTile = (
  activity: ICrossSellActivity,
  ctx: ICrossSellTileContext,
): ICrossSellTile | undefined => {
  const url = buildUrl('activity', activity.searchParams, ctx);
  if (!url) return undefined;
  const name = (activity.name || '').trim();
  return {
    product: 'activity',
    title: name,
    destination: name,
    url,
    startingPrice: extractPrice(activity),
    currency: ctx.currency,
    resultCount: activity.resultCount,
    distanceMiles: activity.distanceMiles,
  };
};

const lodgingTile = (
  product: 'hotel' | 'condo',
  lodging: ICrossSellLodging,
  ctx: ICrossSellTileContext,
): ICrossSellTile | undefined => {
  // Prefer the product-specific deep-link; fall back to the other when the
  // Type-filtered response only populates one of the two fields.
  const searchParams =
    product === 'hotel'
      ? lodging.hotelSearchParams || lodging.condoSearchParams
      : lodging.condoSearchParams || lodging.hotelSearchParams;
  const url = buildUrl(product, searchParams, ctx);
  if (!url) return undefined;
  return {
    product,
    title: (lodging.description || '').trim(),
    destination: (lodging.descriptionFull || lodging.description || '').trim(),
    url,
    startingPrice: extractPrice(lodging),
    currency: ctx.currency,
    // Lodging carries a count per vertical, so pick the one this tile links to.
    resultCount: product === 'hotel' ? lodging.hotelResultCount : lodging.condoResultCount,
    distanceMiles: lodging.distanceMiles,
  };
};

const lodgingHasParams = (product: 'hotel' | 'condo', lodging: ICrossSellLodging): boolean =>
  product === 'hotel'
    ? !!(lodging.hotelSearchParams || lodging.condoSearchParams)
    : !!(lodging.condoSearchParams || lodging.hotelSearchParams);

const finalize = (
  tiles: (ICrossSellTile | undefined)[],
  origin: CrossSellOrigin,
  maxTiles: number,
): ICrossSellTile[] => {
  const seen = new Set<string>();
  const result: ICrossSellTile[] = [];
  for (const tile of tiles) {
    // Exclude the just-booked category and drop empty / duplicate tiles.
    if (!tile || tile.product === origin || seen.has(tile.url)) continue;
    seen.add(tile.url);
    result.push(tile);
    if (result.length >= maxTiles) break;
  }
  return result;
};

/**
 * Extracts the tiles for a single offered `vertical` from one API response.
 * The response shape depends on the booked product ("origin"). Lodging and activity
 * verticals return the single best tile; car verticals return one tile per distinct
 * location of that kind (e.g. an international + a local airport), like the email.
 * The global max-tiles budget (see `finalize`) bounds the total.
 */
export const extractVerticalTiles = (
  origin: CrossSellOrigin,
  vertical: CrossSellProduct,
  response: CrossSellResponse | null | undefined,
  ctx: ICrossSellTileContext,
): (ICrossSellTile | undefined)[] => {
  if (!response) return [];

  if (origin === 'hotel' || origin === 'condo') {
    const places = (response as ILodgingCrossSellResponse).lodgingPlaces ?? [];
    if (vertical === 'carCity' || vertical === 'carAirport') {
      return carTilesByKind(places.flatMap((place) => place.lodgingCars ?? []), vertical, ctx);
    }
    if (vertical === 'activity') {
      const best = pickBest(places.flatMap((place) => place.lodgingActivities ?? []));
      return best ? [activityTile(best, ctx)] : [];
    }
  } else if (origin === 'activity') {
    const places = (response as IActivityCrossSellResponse).lodgingActivities ?? [];
    if (vertical === 'hotel' || vertical === 'condo') {
      const lodgings = places.flatMap((place) => place.lodgings ?? []);
      const withParams = lodgings.filter((l) => lodgingHasParams(vertical, l));
      const best = pickBest(withParams.length ? withParams : lodgings);
      return best ? [lodgingTile(vertical, best, ctx)] : [];
    }
    if (vertical === 'carCity' || vertical === 'carAirport') {
      return carTilesByKind(places.flatMap((place) => place.cars ?? []), vertical, ctx);
    }
  } else if (origin === 'car') {
    const locations = (response as ICarCrossSellResponse).lodgingCars ?? [];
    if (vertical === 'hotel' || vertical === 'condo') {
      const lodgings = locations.flatMap((location) => location.lodgings ?? []);
      const withParams = lodgings.filter((l) => lodgingHasParams(vertical, l));
      const best = pickBest(withParams.length ? withParams : lodgings);
      return best ? [lodgingTile(vertical, best, ctx)] : [];
    }
    if (vertical === 'activity') {
      const best = pickBest(locations.flatMap((location) => location.activities ?? []));
      return best ? [activityTile(best, ctx)] : [];
    }
  }

  return [];
};

/**
 * Builds the tiles to render from the per-vertical prefetch results.
 * Returns at most `maxTiles` tiles, excluding the booked category and duplicates.
 */
export const buildCrossSellTilesFromEntries = (
  origin: CrossSellOrigin,
  entries: ICrossSellEntry[] | null | undefined,
  ctx: ICrossSellTileContext,
  maxTiles: number = DEFAULT_MAX_TILES,
): ICrossSellTile[] => {
  const tiles: (ICrossSellTile | undefined)[] = [];
  for (const entry of entries ?? []) {
    tiles.push(...extractVerticalTiles(origin, entry.vertical, entry.response, ctx));
  }
  return finalize(tiles, origin, maxTiles);
};

/** Reads the account username segment (`/acme`) from the current URL. */
export const getAccountPathFromLocation = (): string => {
  const segment = window.location.pathname.split('/')[1];
  return segment ? `/${segment}` : '';
};

/** Host prefixes that map to the activities subdomain (replaced with `activities.`). */
const RSI_ACTIVITIES_HOST_PREFIXES = ['portal.', 'members.'] as const;

const toActivitiesHost = (host: string): string => {
  const lowerHost = host.toLowerCase();
  const matchedPrefix = RSI_ACTIVITIES_HOST_PREFIXES.find((prefix) => lowerHost.startsWith(prefix));
  return matchedPrefix
    ? `activities.${host.slice(matchedPrefix.length)}`
    : `activities.${host}`;
};

/** Parses an absolute (or protocol-relative) homeUrl for its real host; relative paths return null. */
const extractHostFromHomeUrl = (
  homeUrl?: string | null,
): { protocol: string; host: string } | null => {
  const trimmed = (homeUrl || '').trim();
  if (!trimmed) return null;

  try {
    if (/^https?:\/\//i.test(trimmed)) {
      const parsed = new URL(trimmed);
      return parsed.host ? { protocol: parsed.protocol, host: parsed.host } : null;
    }
    if (trimmed.startsWith('//')) {
      const parsed = new URL(`https:${trimmed}`);
      return parsed.host ? { protocol: window.location.protocol, host: parsed.host } : null;
    }
  } catch {
    return null;
  }

  return null;
};

/**
 * Activities front base URL for cross-app deep-links.
 *
 * 1. `activitiesBaseAddressSite` preference when set.
 * 2. RSI template in prod (no preference): derive from menu `homeUrl` host when
 *    present, otherwise the current host — known prefixes (`portal.`, `members.`)
 *    → `activities.*`, otherwise prefix `activities.`.
 * 3. Otherwise `VITE_ACTIVITIES_URL` (always used outside prod).
 */
export const resolveActivitiesBaseUrl = (
  account: Pick<IAccount, 'isRSITemplate' | 'preferences'> | null | undefined,
  homeUrl?: string | null,
): string => {
  const preference = (getPreferenceFromAccount(account, ACTIVITIES_BASEADDRESSSITE_FIELD_NAME, '') || '').trim();
  if (preference) {
    return preference.replace(/\/$/, '');
  }

  const isProd = import.meta.env.MODE === 'prod';
  if (isProd && account?.isRSITemplate) {
    const fromHomeUrl = extractHostFromHomeUrl(homeUrl);
    const protocol = fromHomeUrl?.protocol ?? window.location.protocol;
    const host = fromHomeUrl?.host ?? window.location.host;
    return `${protocol}//${toActivitiesHost(host)}`;
  }

  return ((import.meta.env.VITE_ACTIVITIES_URL as string) || '').replace(/\/$/, '');
};
